import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Loader2,
  Paperclip,
  Search,
  Send,
  X,
} from 'lucide-react'

import {
  supportInquiryApi,
  type AdminInquiryDetailResponse,
  type InquiryStatus,
  type LcCenterInquiryCategory,
} from '@/api/supportInquiry'
import { lcOperationsInquiryLink } from '../lcOperationsInquiryLink'
import { useAuthStore } from '@/store/authStore'

const CATEGORY_LABELS: Record<LcCenterInquiryCategory, string> = {
  content: '콘텐츠',
  system: '시스템',
  billing: '결제',
  policy: '정책',
  other: '기타',
}

type LcCenterVisibleStatus = Extract<InquiryStatus, 'pending' | 'answered'>

const STATUS_OPTIONS: { value: LcCenterVisibleStatus | ''; label: string }[] = [
  { value: '', label: '전체 상태' },
  { value: 'pending', label: '답변 대기' },
  { value: 'answered', label: '답변 완료' },
]

const CATEGORY_OPTIONS: { value: LcCenterInquiryCategory | ''; label: string }[] = [
  { value: '', label: '전체 카테고리' },
  { value: 'content', label: '콘텐츠' },
  { value: 'system', label: '시스템' },
  { value: 'billing', label: '결제' },
  { value: 'policy', label: '정책' },
  { value: 'other', label: '기타' },
]

const STATUS_LABELS: Record<LcCenterVisibleStatus, string> = {
  pending: '답변 대기',
  answered: '답변 완료',
}

const STATUS_BADGES: Record<LcCenterVisibleStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  answered: 'bg-green-100 text-green-700',
}

function isLcCenterVisibleStatus(status: string): status is LcCenterVisibleStatus {
  return status === 'pending' || status === 'answered'
}

function getStatusDisplay(status: string): {
  label: string
  className: string
  icon: typeof Clock | typeof CheckCircle | null
} {
  if (isLcCenterVisibleStatus(status)) {
    return {
      label: STATUS_LABELS[status],
      className: STATUS_BADGES[status],
      icon: status === 'pending' ? Clock : CheckCircle,
    }
  }

  return {
    label: status,
    className: 'bg-slate-100 text-slate-700',
    icon: null,
  }
}

const MAX_FILES = 3
const MAX_FILE_BYTES = 10 * 1024 * 1024
const PAGE_SIZE = 20
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const
const FILE_ACCEPT = ALLOWED_MIME_TYPES.join(',')
const ALLOWED_FILE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'mp4',
  'webm',
  'mov',
  'pdf',
  'doc',
  'docx',
])

function formatDateTime(value: string | null): string {
  if (!value) return '-'
  return new Date(value).toLocaleString('ko-KR')
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

function getFileValidationMessage(files: File[]): string | null {
  if (files.length > MAX_FILES) {
    return '파일은 최대 3개까지 첨부할 수 있습니다.'
  }
  if (files.some((file) => file.size > MAX_FILE_BYTES)) {
    return '파일 크기는 10MB를 넘을 수 없습니다.'
  }
  if (files.some((file) => {
    const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() ?? '' : ''
    return (
      !ALLOWED_MIME_TYPES.includes(file.type as (typeof ALLOWED_MIME_TYPES)[number]) ||
      !ALLOWED_FILE_EXTENSIONS.has(extension)
    )
  })) {
    return '지원하지 않는 파일 형식입니다.'
  }

  return null
}

function AttachmentList({
  attachments,
}: {
  attachments: AdminInquiryDetailResponse['attachments']
}) {
  if (attachments.length === 0) {
    return <p className="text-sm text-slate-500">첨부파일이 없습니다.</p>
  }

  return (
    <div className="space-y-2">
      {attachments.map((attachment) => (
        <div
          key={attachment.attachmentId}
          className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
        >
          <Paperclip className="h-4 w-4 flex-shrink-0 text-slate-500" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-900">{attachment.fileName}</p>
            <p className="text-xs text-slate-500">{formatFileSize(attachment.fileSize)}</p>
          </div>
          {attachment.downloadUrl && (
            <a
              href={attachment.downloadUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-600 hover:bg-slate-200"
              title="다운로드"
            >
              <Download className="h-4 w-4" />
            </a>
          )}
        </div>
      ))}
    </div>
  )
}

function DetailModal({
  inquiryId,
  onClose,
}: {
  inquiryId: number
  onClose: () => void
}) {
  const isSuperAdmin = useAuthStore((state) => state.user?.isAdminSuper ?? false)
  const queryClient = useQueryClient()
  const [replyContent, setReplyContent] = useState('')
  const [files, setFiles] = useState<File[]>([])

  const { data: detail, isLoading, isError } = useQuery({
    queryKey: ['admin', 'support-inquiries', inquiryId],
    queryFn: () => supportInquiryApi.getById(inquiryId),
  })

  const replyMutation = useMutation({
    mutationFn: () => supportInquiryApi.createReplyWithFiles(inquiryId, replyContent.trim(), files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'lc-center-support-inquiries'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-inquiries', inquiryId] })
      setReplyContent('')
      setFiles([])
    },
  })

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files ?? [])
    const validationMessage = getFileValidationMessage(nextFiles)

    if (validationMessage) {
      alert(validationMessage)
      setFiles([])
      event.target.value = ''
      return
    }

    setFiles(nextFiles)
    event.target.value = ''
  }

  const handleRemoveFile = (index: number) => {
    setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))
  }

  const handleSubmitReply = () => {
    if (!replyContent.trim()) {
      return
    }

    const validationMessage = getFileValidationMessage(files)
    if (validationMessage) {
      alert(validationMessage)
      return
    }

    replyMutation.mutate()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">LC 센터 문의 상세</h2>
            <p className="mt-1 text-sm text-slate-500">문의 확인과 답변 작성</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading && (
          <div className="flex min-h-[320px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
          </div>
        )}

        {isError && (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-center">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <p className="text-sm text-slate-700">문의 상세를 불러오지 못했습니다.</p>
          </div>
        )}

        {detail && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.9fr)]">
                <section className="space-y-6">
                  {isSuperAdmin && detail.organizationId != null ? (
                    <a
                      href={lcOperationsInquiryLink(detail.organizationId)}
                      className="inline-flex rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                    >
                      센터 운영 조회
                    </a>
                  ) : null}
                  <div className="grid gap-4 rounded-xl border border-slate-200 p-4 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        센터명
                      </p>
                      <p className="mt-1 text-sm text-slate-900">{detail.organizationName || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        문의자
                      </p>
                      <p className="mt-1 text-sm text-slate-900">
                        {detail.userName || '-'} ({detail.userEmail || '-'})
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        카테고리
                      </p>
                      <p className="mt-1 text-sm text-slate-900">
                        {CATEGORY_LABELS[detail.category as LcCenterInquiryCategory] || detail.category}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        상태
                      </p>
                      <div className="mt-1">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                        getStatusDisplay(detail.status).className
                      }`}
                        >
                          {(() => {
                            const { icon: StatusIcon, label } = getStatusDisplay(detail.status)
                            return (
                              <>
                                {StatusIcon && <StatusIcon className="h-3.5 w-3.5" />}
                                {label}
                              </>
                            )
                          })()}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        접수일시
                      </p>
                      <p className="mt-1 text-sm text-slate-900">{formatDateTime(detail.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        답변일시
                      </p>
                      <p className="mt-1 text-sm text-slate-900">{formatDateTime(detail.answeredAt)}</p>
                    </div>
                  </div>

                  <section className="rounded-xl border border-slate-200 p-4">
                    <p className="text-sm font-semibold text-slate-900">제목</p>
                    <p className="mt-2 text-base text-slate-900">{detail.title}</p>
                  </section>

                  <section className="rounded-xl border border-slate-200 p-4">
                    <p className="text-sm font-semibold text-slate-900">문의 내용</p>
                    <div className="mt-2 whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-sm text-slate-900">
                      {detail.content}
                    </div>
                  </section>

                  <section className="rounded-xl border border-slate-200 p-4">
                    <p className="text-sm font-semibold text-slate-900">문의 첨부파일</p>
                    <div className="mt-3">
                      <AttachmentList attachments={detail.attachments} />
                    </div>
                  </section>
                </section>

                <section className="space-y-6">
                  <section className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-900">답변 이력</p>
                      <span className="text-xs text-slate-500">{detail.replies.length}건</span>
                    </div>
                    <div className="mt-3 space-y-3">
                      {detail.replies.length === 0 && (
                        <div className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
                          등록된 답변이 없습니다.
                        </div>
                      )}
                      {detail.replies.map((reply) => (
                        <div key={reply.replyId} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-medium text-slate-900">{reply.authorName}</p>
                            <span className="text-xs text-slate-500">{reply.authorRole}</span>
                            <span className="ml-auto text-xs text-slate-500">
                              {formatDateTime(reply.createdAt)}
                            </span>
                          </div>
                          <div className="mt-2 whitespace-pre-wrap text-sm text-slate-900">
                            {reply.content}
                          </div>
                          {reply.attachments.length > 0 && (
                            <div className="mt-3">
                              <AttachmentList attachments={reply.attachments} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <Send className="h-4 w-4" />
                      답변 작성
                    </div>
                    <textarea
                      value={replyContent}
                      onChange={(event) => setReplyContent(event.target.value)}
                      placeholder="센터 문의에 대한 답변을 입력하세요."
                      className="mt-3 min-h-[140px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                        <Paperclip className="h-4 w-4" />
                        파일 첨부
                        <input
                          type="file"
                          multiple
                          accept={FILE_ACCEPT}
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-slate-500">최대 3개, 파일당 10MB</p>
                    </div>

                    {files.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {files.map((file, index) => (
                          <div
                            key={`${file.name}-${file.size}-${index}`}
                            className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2"
                          >
                            <Paperclip className="h-4 w-4 text-slate-500" />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm text-slate-900">{file.name}</p>
                              <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile(index)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                              title="첨부 제거"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={handleSubmitReply}
                        disabled={!replyContent.trim() || replyMutation.isPending}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                      >
                        {replyMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        답변 등록
                      </button>
                    </div>
                  </section>
                </section>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default function LcCenterInquiriesTab() {
  const [page, setPage] = useState(0)
  const [status, setStatus] = useState<LcCenterVisibleStatus | ''>('')
  const [category, setCategory] = useState<LcCenterInquiryCategory | ''>('')
  const [keywordInput, setKeywordInput] = useState('')
  const [keyword, setKeyword] = useState('')
  const [selectedInquiryId, setSelectedInquiryId] = useState<number | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'lc-center-support-inquiries', { page, status, category, keyword }],
    queryFn: () =>
      supportInquiryApi.getList({
        source: 'lc_center',
        status: status || undefined,
        category: category || undefined,
        keyword: keyword || undefined,
        page,
        size: PAGE_SIZE,
      }),
    staleTime: 30_000,
  })

  const handleSearch = () => {
    setPage(0)
    setKeyword(keywordInput.trim())
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-slate-600" />
        <p className="text-slate-600">LC 센터 문의 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <p className="text-slate-900">LC 센터 문의 목록을 불러오지 못했습니다.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600">
            <Clock className="h-4 w-4" />
            <span>{data ? `${data.totalElements}건` : '0건'}</span>
          </div>

          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as LcCenterVisibleStatus | '')
              setPage(0)
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={category}
            onChange={(event) => {
              setCategory(event.target.value as LcCenterInquiryCategory | '')
              setPage(0)
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900"
          >
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="flex min-w-[280px] flex-1 items-center gap-2 rounded-lg border border-slate-300 px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={keywordInput}
              onChange={(event) => setKeywordInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSearch()
                }
              }}
              placeholder="제목, 내용 검색"
              className="w-full border-0 bg-transparent text-sm text-slate-900 outline-none"
            />
          </div>

          <button
            type="button"
            onClick={handleSearch}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
          >
            <Search className="h-4 w-4" />
            검색
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">센터명</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">문의자</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">카테고리</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">제목</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">상태</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">접수일시</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">답변일시</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">
                    LC 센터 문의가 없습니다.
                  </td>
                </tr>
              )}

              {data?.content.map((inquiry) => (
                <tr
                  key={inquiry.inquiryId}
                  onClick={() => setSelectedInquiryId(inquiry.inquiryId)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-4 py-3 text-sm text-slate-900">{inquiry.organizationName || '-'}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-900">{inquiry.userName || '-'}</p>
                    <p className="text-xs text-slate-500">{inquiry.userEmail || '-'}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    {CATEGORY_LABELS[inquiry.category as LcCenterInquiryCategory] || inquiry.category}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="max-w-[320px] truncate text-sm font-medium text-slate-900">
                        {inquiry.title}
                      </span>
                      {inquiry.hasAttachments && <Paperclip className="h-4 w-4 text-slate-400" />}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                        getStatusDisplay(inquiry.status).className
                      }`}
                    >
                      {(() => {
                        const { icon: StatusIcon, label } = getStatusDisplay(inquiry.status)
                        return (
                          <>
                            {StatusIcon && <StatusIcon className="h-3.5 w-3.5" />}
                            {label}
                          </>
                        )
                      })()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatDateTime(inquiry.createdAt)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatDateTime(inquiry.answeredAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalElements)} /{' '}
              {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => current - 1)}
                disabled={page === 0}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((current) => current + 1)}
                disabled={page >= data.totalPages - 1}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedInquiryId !== null && (
        <DetailModal inquiryId={selectedInquiryId} onClose={() => setSelectedInquiryId(null)} />
      )}
    </div>
  )
}
